import {type ChangeEvent, type SyntheticEvent, useState} from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { Mail, Lock, User, Calendar, Smile, Sparkles, CircleCheck, CircleX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AuthLayout, AuthInput, AuthButton } from './AuthComponents';
// import { StravaButton } from './StravaButton';
import { GoogleButton } from './GoogleButton';
import { Gender } from "@/gql/graphql.ts";
import { useAuth } from '@/context/AuthContext';
import { REGISTER_MUTATION } from '@/graphql/mutations';
import { CHECK_USERNAME_AVAILABILITY } from '@/graphql/queries';
import { ErrorBanner } from '@/components/ui/ErrorBanner';

export function Register() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useTranslation('auth');
    const [registerMutation, { loading }] = useMutation(REGISTER_MUTATION);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: '',
        birthDate: '',
        gender: Gender.Male
    });

    const [debouncedUsername] = useDebounce(formData.username, 500);
    const { data: usernameCheck, loading: checkingUsername } = useQuery(
        CHECK_USERNAME_AVAILABILITY,
        {
            variables: { username: debouncedUsername },
            skip: debouncedUsername.length < 3,
        },
    );
    const usernameAvailable = usernameCheck?.checkUsernameAvailability;

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();
        setError('');

        const { firstName, lastName, username, email, password, birthDate } = formData;
        if (!firstName || !lastName || !username || !email || !password || !birthDate) {
            setError(t('register.error.fillAll'));
            return;
        }

        if (password.length < 8) {
            setError(t('register.error.passwordLength'));
            return;
        }

        if (usernameAvailable === false) {
            setError(t('register.username.taken'));
            return;
        }

        try {
            const dateObj = new Date(birthDate);
            const isoDate = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 12, 0, 0)).toISOString();

            const { data } = await registerMutation({
                variables: {
                    input: {
                        ...formData,
                        birthDate: isoDate,
                        gender: formData.gender,
                    }
                }
            });

            if (data?.register?.token) {
                await login(data.register.token);
                navigate('/');
            }
        } catch (err: unknown) {
            console.error(err);
            if (err instanceof Error && err.message.includes('Duplicate')) {
                setError(t('register.error.duplicate'));
            } else {
                setError(t('register.error.generic'));
            }
        }
    };

    return (
        <AuthLayout
            title={t('register.title')}
            subtitle={t('register.subtitle')}
            footer={
                <p className="text-slate-400 text-sm">
                    {t('register.hasAccount')}{' '}
                    <button onClick={() => navigate('/login')} className="text-emerald-600 font-bold hover:underline">
                        {t('register.signIn')}
                    </button>
                </p>
            }
        >
            <div className="space-y-6">
                <GoogleButton label={t('google.signUpWith')} />
                { /*<StravaButton label={t('strava.signUpWith')} />*/ }

                <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-sm font-medium text-slate-400">{t('register.or')}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">

                    {/* LIGNE 1 : Prénom / Nom */}
                    <div className="flex gap-3">
                        <AuthInput
                            label={t('register.firstName.label')}
                            icon={User}
                            name="firstName"
                            placeholder={t('register.firstName.placeholder')}
                            value={formData.firstName}
                            onChange={handleChange}
                            className="w-full"
                        />
                        <AuthInput
                            label={t('register.lastName.label')}
                            icon={User}
                            name="lastName"
                            placeholder={t('register.lastName.placeholder')}
                            value={formData.lastName}
                            onChange={handleChange}
                            className="w-full"
                        />
                    </div>

                    {/* LIGNE 2 : Username + Date Naissance */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                            <AuthInput
                                label={t('register.username.label')}
                                icon={Smile}
                                name="username"
                                placeholder={t('register.username.placeholder')}
                                value={formData.username}
                                onChange={handleChange}
                            />
                            {debouncedUsername.length >= 3 && (
                                <div className="flex items-center gap-1.5 mt-1.5 ml-1">
                                    {checkingUsername ? (
                                        <span className="text-xs text-slate-400">{t('register.username.checking')}</span>
                                    ) : usernameAvailable ? (
                                        <>
                                            <CircleCheck size={14} className="text-emerald-500" />
                                            <span className="text-xs text-emerald-600 font-medium">{t('register.username.available')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <CircleX size={14} className="text-red-500" />
                                            <span className="text-xs text-red-600 font-medium">{t('register.username.taken')}</span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="sm:w-1/2">
                            <AuthInput
                                label={t('register.birthDate.label')}
                                icon={Calendar}
                                name="birthDate"
                                type="date"
                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 13)).toISOString().split('T')[0]}
                                value={formData.birthDate}
                                onChange={handleChange}
                                className="text-sm"
                            />
                        </div>
                    </div>

                    {/* LIGNE 3 : Email */}
                    <AuthInput
                        label={t('register.email.label')}
                        icon={Mail}
                        name="email"
                        type="email"
                        placeholder={t('register.email.placeholder')}
                        value={formData.email}
                        onChange={handleChange}
                    />

                    {/* LIGNE 4 : Genre */}
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-slate-700 ml-1">{t('register.gender.label')}</label>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, gender: Gender.Male })}
                                className={cn(
                                    "flex-1 h-12 rounded-xl border-2 font-bold text-sm transition-all active:scale-95",
                                    formData.gender === Gender.Male
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                        : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                                )}
                            >
                                {t('register.gender.male')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, gender: Gender.Female })}
                                className={cn(
                                    "flex-1 h-12 rounded-xl border-2 font-bold text-sm transition-all active:scale-95",
                                    formData.gender === Gender.Female
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                        : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                                )}
                            >
                                {t('register.gender.female')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, gender: Gender.Other })}
                                className={cn(
                                    "flex-1 h-12 rounded-xl border-2 font-bold text-sm transition-all active:scale-95",
                                    formData.gender === Gender.Other
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                        : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                                )}
                            >
                                {t('register.gender.other')}
                            </button>
                        </div>
                    </div>

                    {/* LIGNE 5 : Password */}
                    <AuthInput
                        label={t('register.password.label')}
                        icon={Lock}
                        name="password"
                        type="password"
                        placeholder={t('register.password.placeholder')}
                        value={formData.password}
                        onChange={handleChange}
                    />

                    {/* Feedback Erreur */}
                    {error && <ErrorBanner message={error} className="animate-in shake" />}

                    {/* Submit */}
                    <div className="pt-2">
                        <AuthButton type="submit" isLoading={loading}>
                            {t('register.submit')} <Sparkles size={20} />
                        </AuthButton>
                    </div>

                    <p className="text-center text-[10px] text-slate-400 px-4">
                        {t('register.legalPrefix')}{' '}
                        <Link to="/terms" className="underline hover:text-slate-600">{t('register.legalTerms')}</Link>
                        {' '}{t('register.legalAnd')}{' '}
                        <Link to="/privacy" className="underline hover:text-slate-600">{t('register.legalPrivacy')}</Link>.
                    </p>
                </form>
            </div>
        </AuthLayout>
    );
}
